import { Middleware, Dispatch } from 'redux';
import { getType, ActionType } from 'typesafe-actions';
import _ from 'lodash';
import { DefaultGridLayout, Layouts, BlockConfiguration, LayoutWithType, BlockLayouts } from '@tradingblock/types';
import { DataState } from '../state';
import { RootAction, BlockActions } from '../actions';
import { Dispatcher } from '../dispatcher';
import { generateBlockClosestFitLayout } from '../../../utilities/block';

// reference https://redux.js.org/advanced/middleware/ for best practices

type blockActions = ActionType<typeof BlockActions.addBlock> | ActionType<typeof BlockActions.addOrUpdateBlock>;
const handleAddBlock = (
  state: DataState,
  prevState: DataState,
  dispatch: Dispatch<RootAction>,
  action: blockActions
) => {
  const dispatcher = Dispatcher(dispatch);
  const block = { ...action.payload.block };
  const { blocks } = state.blocks;
  const { blocks: prevBlocks } = prevState.blocks;
  const layout = { ...DefaultGridLayout, ...state.layout };
  const { breakpoint, layouts } = { ...DefaultGridLayout, ...layout };

  // Initialize layouts if they don't exist
  const currLayouts = layouts && layouts[breakpoint] ? layouts[breakpoint] : [];
  let blockLayouts: BlockLayouts = {
    ...block.layouts,
  };

  // Always generate a new layout for the block in a new dashboard
  const newLayout = generateBlockClosestFitLayout(block.type, currLayouts, layout);

  // Ensure we have layouts for all breakpoints
  const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
  blockLayouts = breakpoints.reduce(
    (acc, bp) => ({
      ...acc,
      [bp]: {
        ...newLayout,
        i: block.blockId,
        type: block.type,
      },
    }),
    {}
  );

  const nextLayouts = updateLayouts(layouts, blocks, blockLayouts);

  // Ensure the layout is persisted
  dispatcher.layout.setAllLayouts(nextLayouts, { persist: true });

  return state;
};

const handleRemoveBlock = (
  state: DataState,
  prevState: DataState,
  dispatch: Dispatch<RootAction>,
  action: ActionType<typeof BlockActions.removeBlock>
) => {
  const dispatcher = Dispatcher(dispatch);
  const { blocks } = state.blocks;
  const { layouts } = { ...DefaultGridLayout, ...state.layout };

  const nextLayouts = _.reduce(
    _.isEmpty(layouts) ? { lg: [], md: [], sm: [], xs: [], xxs: [] } : layouts,
    (acc: Layouts, breakpointLayouts, key) => {
      return {
        ...acc,
        [key]: _.filter(breakpointLayouts, bl => _.some(blocks, b => bl.i === b.blockId)),
      };
    },
    {}
  );

  dispatcher.layout.setAllLayouts(nextLayouts, { persist: true });

  return state;
};

const updateLayouts = (layouts: Layouts | undefined, blocks: BlockConfiguration[], blockLayouts?: BlockLayouts) => {
  const defaultLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };
  const baseLayouts = _.isEmpty(layouts) ? defaultLayouts : layouts;

  const updatedLayouts = _.reduce(
    baseLayouts,
    (acc: Layouts, breakpointLayouts, key) => {
      const blockLayout = blockLayouts && (blockLayouts[key] || blockLayouts.lg);
      const currentLayouts = breakpointLayouts || [];

      return {
        ...acc,
        [key]: getUpdatedLayouts('ADD', currentLayouts, blockLayout),
      };
    },
    {}
  );

  return updatedLayouts;
};

const getUpdatedLayouts = (mode: 'ADD' | 'REM', layouts: LayoutWithType[], blockLayout: LayoutWithType | undefined) => {
  const newLayouts =
    mode === 'ADD' && blockLayout && blockLayout.i && !_.some(layouts, l => l.i === blockLayout.i) ? [blockLayout] : [];
  return _.reduce(
    [...layouts, ...newLayouts],
    (acc: LayoutWithType[], layout) => {
      if (blockLayout && blockLayout.i && blockLayout.i === layout.i) {
        return [...acc, blockLayout];
      }
      return [...acc, layout];
    },
    []
  );
};

export const layoutMiddleware: Middleware<Dispatch<RootAction>, DataState, Dispatch<RootAction>> = middleware => (
  next: Dispatch<RootAction>
) => (action: RootAction) => {
  try {
    const prevState = middleware.getState();
    const result = next(action);
    const nextState = middleware.getState();
    switch (action.type) {
      case getType(BlockActions.addOrUpdateBlock):
      case getType(BlockActions.addBlock):
        handleAddBlock(nextState, prevState, middleware.dispatch, action);
        break;
      case getType(BlockActions.removeBlock):
        handleRemoveBlock(nextState, prevState, middleware.dispatch, action);
        break;
    }
    return result;
  } catch (err) {
    console.error('layoutMiddleware :: Caught an exception for action ', action, err);
  }
};
